import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Modal, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { useReservation } from '../components/ReservationContext';
import { FaCreditCard, FaCcVisa, FaCcMastercard, FaCcAmex } from 'react-icons/fa';

import '../css/Step4.css'
import {
  Elements,
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { api } from '../components/api';
import StepNavigation from '../components/StepsNavigation';
import TermsModal from '../components/Termsmodel';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

function PaymentForm({ onSuccess, setStepNavigationProps }) {
  const { reservation } = useReservation();
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showProcessing, setShowProcessing] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);


  useEffect(() => {
    setStepNavigationProps({
      onContinue: handleSubmit,
      disableContinue: !stripe || loading,
      continueLabel: loading ? 'Processing...' : 'Confirm',
      backLabel: 'Back',
      onBack: () => navigate('/contact'),
      hideBack: false,
    });

    return () => {
      setStepNavigationProps({});
    };
  }, [setStepNavigationProps, stripe, loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setLoading(true);
    setShowProcessing(true);

    if (!stripe || !elements) {
      setSubmitError('Stripe has not loaded yet.');
      setShowErrorModal(true);
      setLoading(false);
      setShowProcessing(false);
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardNumberElement,
    });

    if (error) {
      setSubmitError(error.message || 'Card error');
      setShowErrorModal(true);
      setLoading(false);
      setShowProcessing(false);
      return;
    }

    const payload = {
      reservationId: reservation.reservationId,
      paymentMethodId: paymentMethod.id,
    };

    try {
      const response = await api.post('/reservation/confirm', payload);
      const { customerSecrete, requiredAction } = response.data;

      if (requiredAction && customerSecrete) {
        const result = await stripe.confirmCardSetup(customerSecrete, {
          payment_method: paymentMethod.id,
        });

        if (result.error) {
          setSubmitError(result.error.message || '3D Secure failed');
          setShowErrorModal(true);
          setLoading(false);
          setShowProcessing(false);
          return;
        }

        await api.post('/reservation/confirm-complete', {
          reservationId: reservation.reservationId,
          paymentMethodId: paymentMethod.id,
          setupIntentId: result.setupIntent.id,
        });
      }

      onSuccess();
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        'Error submitting reservation. Please try again.';
      setSubmitError(errorMessage);
      setShowErrorModal(true);
    } finally {
      setLoading(false);
      setShowProcessing(false);
    }
  };

  const cardStyle = {
    style: {
      base: {
        fontSize: '16px',
        color: '#32325d',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#fa755a',
      },
    },
  };

  return (
    <>
      <Form>
  <div className="bg-white p-3 p-sm-4 rounded shadow-sm">

 <div className="mb-3 d-flex  gap-3 fs-3 text-black-50">
    
  <FaCreditCard title="Generic Card" />
    </div>
    <div className="mb-3">
      <div className="stripe-input form-control p-2">
        <CardNumberElement options={cardStyle} />
      </div>
    </div>

    <div className="mb-3">
      <div className="stripe-input form-control p-2">
        <CardExpiryElement options={cardStyle} />
      </div>
    </div>

    <div className="mb-4">
      <div className="stripe-input form-control p-2">
        <CardCvcElement options={cardStyle} />
      </div>
    </div>

    <div className="mt-4">
      <h6 className="fw-semibold">Cancellation Policy</h6>
     <p className="small text-muted mb-3">
  Cancellations made 30 or more days before check-in will not incur any charges. 
  Cancellations made 30 to 14 days before check-in will incur a 50% charge of the total reservation amount plus taxes. 
  Cancellations made within 14 days of check-in, as well as no-shows, will be charged in full plus taxes.
</p>

<p className="text-muted small mt-3">
  By continuing, you agree to our{' '}
 <a
  href="#"
  className="text-decoration-none ms-1"
  onClick={(e) => {
    e.preventDefault();
    setShowTermsModal(true);
  }}
>
  Terms and Conditions
</a>

</p>



      <h6 className="fw-semibold">Security & Payments</h6>
      <p className="text-muted small mb-3">
        This site is protected by reCAPTCHA. Google's
        <a href="https://policies.google.com/privacy" className="text-decoration-none ms-1" target="_blank" rel="noreferrer">Privacy Policy</a> and
        <a href="https://policies.google.com/terms" className="text-decoration-none ms-1" target="_blank" rel="noreferrer">Terms of Service</a> apply.
      </p>

     
    </div>

    <div className="d-flex flex-wrap  align-items-center gap-3">
  <img src="/assets/pci-dss-certified.png" alt="PCI Secure" height="40" />
  <img src="/assets/stripe-payment-secure.jpg" alt="Stripe Secure" height="40" />
</div>

  </div>
</Form>


      {/* Modal for Processing */}
      <Modal show={showProcessing} backdrop="static" centered>
        <Modal.Body className="text-center p-5">
          <Spinner animation="border" variant="dark" />
          <div className="mt-3">Processing payment, please wait...</div>
        </Modal.Body>
      </Modal>

      {/* Modal for Error */}
      <Modal show={showErrorModal} onHide={() => setShowErrorModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Payment Error</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-dark">
          {submitError}
        </Modal.Body>
      </Modal>

            <TermsModal show={showTermsModal} onClose={() => setShowTermsModal(false)} />


 

    </>
  );
}

export default function PaymentStep({ setStepNavigationProps }) {
  const navigate = useNavigate();
  const { reservation } = useReservation();

  return (
    <div className="p-3">
    
     <div className="mb-4">
  <h2 className="h5 fw-semibold text-dark mb-3" style={{ fontFamily: 'Montserrat, sans-serif' }}>
Almost there! Let’s wrap it up.
  </h2>
  <p className="text-muted mb-2" style={{ fontSize: '1rem', lineHeight: '1.6' }}>
    To secure your reservation, please provide your valid credit card details. 
    </p>
    <p className="text-muted mb-2" style={{ fontSize: '1rem', lineHeight: '1.6' }}>
  Rest assured, your card will not be charged until your arrival. 
  </p>
  <p className="text-muted mb-0" style={{ fontSize: '1rem', lineHeight: '1.6' }}>
   All transactions are safe and encrypted. If you prefer not to provide a credit card online, please feel free to call us directly at{' '}
    <a
      href="tel:+17056352400"
      className="fw-semibold text-dark text-decoration-none"
    >
      +1 705-635-2400
    </a>.
  </p>
</div>


      <Card className="p-4">
        <Elements stripe={stripePromise}>
          <PaymentForm
            onSuccess={() =>
              navigate('/confirmation', {
                state: { reservation },
              })
            }
            setStepNavigationProps={setStepNavigationProps}
          />
        </Elements>
      </Card>
    </div>
  );
}
